import _ from 'lodash';
import { TravelPolicyActions, TravelPolicyPolicies } from '../constants';

const collectTotalAmountChecks = (trip, policies) => {
  const totalAmountPolicy = _.find(policies, { policy: TravelPolicyPolicies.TotalAmount });
  if (!totalAmountPolicy || !totalAmountPolicy.value) return [];

  const configs = [
    {
      itineraries: trip && trip.flightItinerary ? [trip.flightItinerary] : [],
      getCost: (i) => i.cost && i.cost.price,
      type: 'Flight',
    },
    {
      itineraries: trip && trip.hotelItineraries ? Object.values(trip.hotelItineraries) : [],
      getCost: (i) =>
        (i.selectedProduct &&
          i.selectedProduct.convertedRoomPrice &&
          i.selectedProduct.convertedRoomPrice.originalPrice) ||
        (i.selectedProduct &&
          i.selectedProduct.roomPrice &&
          i.selectedProduct.roomPrice.originalPrice),
      type: 'Hotel',
    },
    {
      itineraries:
        trip && trip.rentalCarItineraries ? Object.values(trip.rentalCarItineraries) : [],
      getCost: (i) => i.cost && i.cost.price,
      type: 'Car',
    },
  ];

  return configs.reduce((checks, { itineraries, getCost, type }) => {
    itineraries.forEach((itinerary) => {
      const cost = getCost(itinerary);
      if (cost !== undefined) {
        checks.push({
          action: totalAmountPolicy.action,
          exceeded: cost > parseFloat(totalAmountPolicy.value),
          message: `${type} total ($${cost}) exceeds policy limit ($${totalAmountPolicy.value})`,
        });
      }
    });
    return checks;
  }, []);
};

const collectHotelNightChecks = (trip, policies) => {
  const numberOfNightsPolicy = _.find(policies, { policy: TravelPolicyPolicies.NumberOfNights });
  if (!numberOfNightsPolicy || !numberOfNightsPolicy.value || !numberOfNightsPolicy.policy)
    return [];

  const hotelItineraries =
    trip && trip.hotelItineraries ? Object.values(trip.hotelItineraries) : [];
  return hotelItineraries
    .filter((itinerary) => itinerary && itinerary.duration)
    .map((itinerary) => ({
      action: numberOfNightsPolicy.action,
      exceeded: itinerary.duration > parseFloat(numberOfNightsPolicy.value),
      message: `Hotel stay (${itinerary.duration} nights) exceeds limit of ${numberOfNightsPolicy.value}`,
    }));
};

const collectHotelRateCodeChecks = (trip, policies) => {
  const hotelRateCodePolicies = _.filter(policies, { policy: TravelPolicyPolicies.HotelRateCode });

  if (!hotelRateCodePolicies || !hotelRateCodePolicies.length) return [];

  const hotelItineraries =
    trip && trip.hotelItineraries ? Object.values(trip.hotelItineraries) : [];

  return hotelItineraries
    .map((itinerary) => {
      const rateCode =
        itinerary.selectedProduct &&
        itinerary.selectedProduct.rooms &&
        itinerary.selectedProduct.rooms[0] &&
        (itinerary.selectedProduct.rooms[0].ratePlanClientId ||
          itinerary.selectedProduct.rooms[0].ratePlanCode);
      const hotelRateCodePolicy = rateCode
        ? _.find(hotelRateCodePolicies, { value: rateCode })
        : undefined;

      if (!hotelRateCodePolicy || !hotelRateCodePolicy.action) return null;

      let message = `Hotel Rate Code - ${rateCode} needs approval from advisor`;
      if (hotelRateCodePolicy.action === TravelPolicyActions.ShowMessageAndAllowBook) {
        message = `Hotel Rate Code - ${rateCode} needs approval from advisor`;
      } else if (hotelRateCodePolicy.action === TravelPolicyActions.Deny) {
        message = `Hotel Rate Code - ${rateCode} is not allowed`;
      }
      return hotelRateCodePolicy
        ? {
            action: hotelRateCodePolicy.action,
            exceeded: true,
            message,
          }
        : null;
    })
    .filter((check) => check !== null);
};

const collectRateThresholdChecks = (trip, policies) => {
  const checks = [];
  const hotelNightlyRateThreshold = _.find(policies, {
    policy: TravelPolicyPolicies.HotelNightlyRateThreshold,
  });
  const roundTripflightRateThreshold = _.find(policies, {
    policy: TravelPolicyPolicies.RoundTripFlightRateThreshold,
  });

  if (hotelNightlyRateThreshold && hotelNightlyRateThreshold.value > 0) {
    const hotelItineraries =
      trip && trip.hotelItineraries ? Object.values(trip.hotelItineraries) : [];
    hotelItineraries.forEach((itinerary) => {
      const rate =
        (itinerary.selectedProduct &&
          itinerary.selectedProduct.convertedRoomPrice &&
          itinerary.selectedProduct.convertedRoomPrice.averageNightlyRate) ||
        (itinerary.selectedProduct &&
          itinerary.selectedProduct.roomPrice &&
          itinerary.selectedProduct.roomPrice.averageNightlyRate);
      if (rate) {
        checks.push({
          action: hotelNightlyRateThreshold.action,
          exceeded: rate > parseFloat(hotelNightlyRateThreshold.value),
          message: `Hotel ${itinerary.hotel.name} per night rate exceeded threshold ($${hotelNightlyRateThreshold.value}). ${hotelNightlyRateThreshold.message}`,
        });
      }
    });
  }

  if (
    roundTripflightRateThreshold &&
    roundTripflightRateThreshold.value > 0 &&
    roundTripflightRateThreshold.action &&
    trip &&
    trip.flightItinerary &&
    !trip.flightItinerary.isOneWay
  ) {
    const cost = trip.flightItinerary.cost && trip.flightItinerary.cost.price;
    if (cost) {
      checks.push({
        action: roundTripflightRateThreshold.action,
        exceeded: cost > parseFloat(roundTripflightRateThreshold.value),
        message: `Roundtrip flight rate exceeds threshold ($${roundTripflightRateThreshold.value}). ${roundTripflightRateThreshold.message}`,
      });
    }
  }

  return checks;
};

const checkTravelPolicies = (trip, policies) => {
  const allChecks = [
    ...collectTotalAmountChecks(trip, policies),
    ...collectHotelNightChecks(trip, policies),
    ...collectRateThresholdChecks(trip, policies),
    ...collectHotelRateCodeChecks(trip, policies),
  ].filter((c) => c.exceeded);

  const status = {
    disabled: false,
    label: '',
    tooltip: '',
    modalContent: '',
    requiresModal: false,
    deniedReasons: [],
    warningReasons: [],
    requestReasons: [],
    actions: [],
  };

  allChecks.forEach(({ action, message }) => {
    if (action === TravelPolicyActions.Deny) {
      status.deniedReasons.push(message);
      if (!status.actions.includes(TravelPolicyActions.Deny)) {
        status.actions.push(TravelPolicyActions.Deny);
      }
    } else if (action === TravelPolicyActions.RequiresApproval) {
      status.requestReasons.push(message);
      status.label = 'Request to Book';
      if (!status.actions.includes(TravelPolicyActions.RequiresApproval)) {
        status.actions.push(TravelPolicyActions.RequiresApproval);
      }
    } else if (action === TravelPolicyActions.ShowMessageAndAllowBook) {
      status.warningReasons.push(message);
      status.requiresModal = true;
      if (!status.actions.includes(TravelPolicyActions.ShowMessageAndAllowBook)) {
        status.actions.push(TravelPolicyActions.ShowMessageAndAllowBook);
      }
    }
  });

  status.disabled = status.deniedReasons.length > 0;

  status.tooltip = [
    ...status.deniedReasons,
    ...(status.disabled ? [] : status.requestReasons),
  ].join('\n');

  status.modalContent = status.warningReasons.join('\n');
  return {
    buttonState: {
      disabled: status.disabled,
      label: status.label,
      tooltip: status.tooltip,
    },
    policyStatus: status,
  };
};

export default checkTravelPolicies;
